import { supabase } from '../lib/supabase'
import { AircraftMovement, ContainerType } from '../types/database'
import { Aircraft } from '../types/intellijetnet'

/**
 * Audit trail entry for movement history
 */
export interface MovementAuditEntry {
  audit_id: string
  movement_id: string
  aircraft_id: number
  model_id: number
  
  // Movement details
  from_container: ContainerType
  to_container: ContainerType
  action_type: 'move' | 'undo' | 'confirm' | 'expire'
  
  // Context
  user_id?: string | null
  user_name?: string | null
  reason?: string | null
  notes?: string | null
  
  // System context
  api_synchronized: boolean
  validation_passed: boolean
  validation_warnings?: string[] | null
  
  // Timestamps
  action_timestamp: string
  created_at: string
}

/**
 * Movement history summary for reporting
 */
export interface MovementHistorySummary {
  aircraft_id: number
  aircraft_info: {
    make: string
    model: string
    sernbr: string
    regnbr: string
  }
  total_movements: number
  successful_movements: number
  undone_movements: number
  expired_movements: number
  last_movement_date: string
  current_container: ContainerType
  movements: MovementAuditEntry[]
}

/**
 * Service for tracking and auditing aircraft movements
 */
export class MovementAuditService {
  private static readonly AUDIT_TABLE = 'movement_audit_log'
  
  /**
   * Log a movement action to the audit trail
   */
  static async logMovementAction(
    movement: AircraftMovement,
    actionType: 'move' | 'undo' | 'confirm' | 'expire',
    context: {
      userId?: string
      userName?: string
      reason?: string
      notes?: string
      apiSynchronized?: boolean
      validationPassed?: boolean
      validationWarnings?: string[]
    } = {}
  ): Promise<void> {
    try {
      const auditEntry = {
        movement_id: movement.movement_id,
        aircraft_id: movement.aircraft_id,
        model_id: movement.model_id,
        from_container: movement.from_container,
        to_container: movement.to_container,
        action_type: actionType,
        user_id: context.userId,
        user_name: context.userName,
        reason: context.reason || movement.movement_reason,
        notes: context.notes || movement.notes,
        api_synchronized: context.apiSynchronized || false,
        validation_passed: context.validationPassed || true,
        validation_warnings: context.validationWarnings,
        action_timestamp: new Date().toISOString()
      }
      
      const { error } = await supabase
        .from(this.AUDIT_TABLE)
        .insert(auditEntry)
      
      if (error) {
        // Error logging movement action - don't throw - audit logging shouldn't break the main flow
      }

    } catch (error) {
      // Error in logMovementAction - silently ignore
    }
  }
  
  /**
   * Get movement history for an aircraft
   */
  static async getAircraftMovementHistory(aircraftId: number): Promise<MovementAuditEntry[]> {
    try {
      const { data, error } = await supabase
        .from(this.AUDIT_TABLE)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('action_timestamp', { ascending: false })
      
      if (error) {
        throw new Error(`Failed to fetch movement history: ${error.message}`)
      }

      return data || []

    } catch (error) {
      throw error
    }
  }
  
  /**
   * Get movement history for a model
   */
  static async getModelMovementHistory(
    modelId: number,
    limit: number = 100,
    offset: number = 0
  ): Promise<MovementAuditEntry[]> {
    try {
      const { data, error } = await supabase
        .from(this.AUDIT_TABLE)
        .select('*')
        .eq('model_id', modelId)
        .order('action_timestamp', { ascending: false })
        .range(offset, offset + limit - 1)
      
      if (error) {
        throw new Error(`Failed to fetch movement history: ${error.message}`)
      }

      return data || []

    } catch (error) {
      throw error
    }
  }
  
  /**
   * Get movement history summary for an aircraft
   */
  static async getAircraftMovementSummary(aircraft: Aircraft): Promise<MovementHistorySummary> {
    try {
      const movements = await this.getAircraftMovementHistory(aircraft.aircraft_id)
      
      const summary: MovementHistorySummary = {
        aircraft_id: aircraft.aircraft_id,
        aircraft_info: {
          make: aircraft.make || '',
          model: aircraft.model || '',
          sernbr: aircraft.sernbr || '',
          regnbr: aircraft.regnbr || ''
        },
        total_movements: movements.length,
        successful_movements: movements.filter(m => m.action_type === 'confirm').length,
        undone_movements: movements.filter(m => m.action_type === 'undo').length,
        expired_movements: movements.filter(m => m.action_type === 'expire').length,
        last_movement_date: movements.length > 0 ? movements[0].action_timestamp : '',
        current_container: movements.length > 0 ? movements[0].to_container : 'for_sale',
        movements
      }
      
      return summary
      
    } catch (error) {
      throw error
    }
  }
  
  /**
   * Get movement statistics for a model
   */
  static async getModelMovementStats(modelId: number, days: number = 30): Promise<{
    totalMovements: number
    successfulMovements: number
    undoneMovements: number
    expiredMovements: number
    uniqueAircraft: number
    averageMovementsPerAircraft: number
    mostActiveAircraft: { aircraft_id: number; movement_count: number }[]
    movementsByContainer: Record<string, number>
    movementsByDay: { date: string; count: number }[]
  }> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
      
      const { data, error } = await supabase
        .from(this.AUDIT_TABLE)
        .select('*')
        .eq('model_id', modelId)
        .gte('action_timestamp', cutoffDate)
        .order('action_timestamp', { ascending: false })
      
      if (error) {
        throw new Error(`Failed to fetch movement stats: ${error.message}`)
      }
      
      const movements = data || []
      
      // Calculate statistics
      const uniqueAircraft = new Set(movements.map(m => m.aircraft_id)).size
      const aircraftMovementCounts = new Map<number, number>()
      const containerCounts = new Map<string, number>()
      const dailyCounts = new Map<string, number>()
      
      movements.forEach(movement => {
        // Aircraft movement counts
        const currentCount = aircraftMovementCounts.get(movement.aircraft_id) || 0
        aircraftMovementCounts.set(movement.aircraft_id, currentCount + 1)
        
        // Container counts
        const containerKey = `${movement.from_container}_to_${movement.to_container}`
        const containerCount = containerCounts.get(containerKey) || 0
        containerCounts.set(containerKey, containerCount + 1)
        
        // Daily counts
        const date = movement.action_timestamp.split('T')[0]
        const dayCount = dailyCounts.get(date) || 0
        dailyCounts.set(date, dayCount + 1)
      })
      
      // Most active aircraft
      const mostActiveAircraft = Array.from(aircraftMovementCounts.entries())
        .map(([aircraft_id, movement_count]) => ({ aircraft_id, movement_count }))
        .sort((a, b) => b.movement_count - a.movement_count)
        .slice(0, 10)
      
      // Movements by day
      const movementsByDay = Array.from(dailyCounts.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date))
      
      return {
        totalMovements: movements.length,
        successfulMovements: movements.filter(m => m.action_type === 'confirm').length,
        undoneMovements: movements.filter(m => m.action_type === 'undo').length,
        expiredMovements: movements.filter(m => m.action_type === 'expire').length,
        uniqueAircraft,
        averageMovementsPerAircraft: uniqueAircraft > 0 ? movements.length / uniqueAircraft : 0,
        mostActiveAircraft,
        movementsByContainer: Object.fromEntries(containerCounts),
        movementsByDay
      }
      
    } catch (error) {
      throw error
    }
  }
  
  /**
   * Search movement history with filters
   */
  static async searchMovementHistory(filters: {
    modelId?: number
    aircraftId?: number
    userId?: string
    actionType?: 'move' | 'undo' | 'confirm' | 'expire'
    fromContainer?: ContainerType
    toContainer?: ContainerType
    startDate?: string
    endDate?: string
    limit?: number
    offset?: number
  }): Promise<MovementAuditEntry[]> {
    try {
      let query = supabase.from(this.AUDIT_TABLE).select('*')
      
      // Apply filters
      if (filters.modelId) {
        query = query.eq('model_id', filters.modelId)
      }
      if (filters.aircraftId) {
        query = query.eq('aircraft_id', filters.aircraftId)
      }
      if (filters.userId) {
        query = query.eq('user_id', filters.userId)
      }
      if (filters.actionType) {
        query = query.eq('action_type', filters.actionType)
      }
      if (filters.fromContainer) {
        query = query.eq('from_container', filters.fromContainer)
      }
      if (filters.toContainer) {
        query = query.eq('to_container', filters.toContainer)
      }
      if (filters.startDate) {
        query = query.gte('action_timestamp', filters.startDate)
      }
      if (filters.endDate) {
        query = query.lte('action_timestamp', filters.endDate)
      }
      
      // Apply pagination
      const limit = filters.limit || 100
      const offset = filters.offset || 0
      query = query.range(offset, offset + limit - 1)
      
      // Order by timestamp
      query = query.order('action_timestamp', { ascending: false })
      
      const { data, error } = await query
      
      if (error) {
        throw new Error(`Failed to search movement history: ${error.message}`)
      }
      
      return data || []
      
    } catch (error) {
      throw error
    }
  }
  
  /**
   * Export movement history to CSV format
   */
  static async exportMovementHistory(
    modelId: number,
    startDate?: string,
    endDate?: string
  ): Promise<string> {
    try {
      const movements = await this.searchMovementHistory({
        modelId,
        startDate,
        endDate,
        limit: 10000 // Large limit for export
      })
      
      // CSV headers
      const headers = [
        'Aircraft ID',
        'Make',
        'Model',
        'Serial Number',
        'Registration',
        'Action Type',
        'From Container',
        'To Container',
        'User',
        'Reason',
        'Notes',
        'API Synchronized',
        'Validation Passed',
        'Timestamp'
      ]
      
      // CSV rows
      const rows = movements.map(movement => [
        movement.aircraft_id,
        '', // Make - would need to join with aircraft data
        '', // Model - would need to join with aircraft data
        '', // Serial Number - would need to join with aircraft data
        '', // Registration - would need to join with aircraft data
        movement.action_type,
        movement.from_container,
        movement.to_container,
        movement.user_name || movement.user_id || '',
        movement.reason || '',
        movement.notes || '',
        movement.api_synchronized ? 'Yes' : 'No',
        movement.validation_passed ? 'Yes' : 'No',
        movement.action_timestamp
      ])
      
      // Combine headers and rows
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n')
      
      return csvContent
      
    } catch (error) {
      throw error
    }
  }
}
